import { memo, useCallback, useState } from "react";
import { useAluguel } from "./useAluguel";
import { aluguelService } from "./aluguelService";
import Swal from "sweetalert2";
import styles from "./Aluguel.module.scss";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const fmtDate = (str) => {
  if (!str) return "—";
  const d = new Date(str.replace(" ", "T"));
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const fmtDateTime = (str) => {
  if (!str) return "—";
  const d = new Date(str.replace(" ", "T"));
  if (isNaN(d)) return "—";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtCPF = (v = "") =>
  v
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    .slice(0, 14);

const fmtTel = (v = "") =>
  v
    .replace(/\D/g, "")
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .slice(0, 15);

const STATUS_CONFIG = {
  reservado: { label: "Reservado" },
  confirmado: { label: "Confirmado" },
  em_andamento: { label: "Em andamento" },
  devolvido: { label: "Devolvido" },
  cancelado: { label: "Cancelado" },
};

const FORMAS = ["Pix", "Dinheiro", "Cartão Crédito", "Cartão Débito"];
const TIPOS_PGTO = [
  { value: "sinal", label: "Sinal" },
  { value: "parcela", label: "Parcela" },
  { value: "quitacao", label: "Quitação" },
];

// Próximas transições válidas por status
const PROXIMAS_ACOES = {
  reservado: [
    { status: "confirmado", label: "Confirmar", icon: "✓" },
    { status: "cancelado", label: "Cancelar", icon: "✕" },
  ],
  confirmado: [
    { status: "em_andamento", label: "Retirada", icon: "▶" },
    { status: "cancelado", label: "Cancelar", icon: "✕" },
  ],
  em_andamento: [
    { status: "devolvido", label: "Devolvido", icon: "↩" },
    { status: "cancelado", label: "Cancelar", icon: "✕" },
  ],
  devolvido: [],
  cancelado: [],
};

// ─── Sub-componentes ──────────────────────────────────────────────────────────

const StatusBadge = memo(({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status };
  const cls = styles[`statusBadge__${status}`] || styles.statusBadge__default;
  return <span className={`${styles.statusBadge} ${cls}`}>{cfg.label}</span>;
});
StatusBadge.displayName = "StatusBadge";

const SaldoBadge = memo(({ total, pago }) => {
  const saldo = Number(total) - Number(pago);
  const quitado = saldo <= 0;
  return (
    <span
      className={`${styles.saldoBadge} ${quitado ? styles.saldoBadge__quitado : styles.saldoBadge__aberto}`}
    >
      {quitado ? "✓ Quitado" : `Falta ${fmtBRL(saldo)}`}
    </span>
  );
});
SaldoBadge.displayName = "SaldoBadge";

// ── Abas ──
const AbasTabs = memo(({ value, onChange, counts }) => (
  <div className={styles.tipoTabs} role="tablist" aria-label="Filtrar aluguéis">
    {[
      { key: "ativos", label: "Em andamento", icon: "📋" },
      { key: "historico", label: "Histórico", icon: "📁" },
    ].map(({ key, label, icon }) => (
      <button
        key={key}
        role="tab"
        aria-selected={value === key}
        className={`${styles.tipoTab} ${value === key ? styles.tipoTabActive : ""}`}
        onClick={() => onChange(key)}
        type="button"
      >
        <span aria-hidden="true">{icon}</span>
        {label}
        <span className={styles.tipoTabCount}>{counts[key] ?? 0}</span>
      </button>
    ))}
  </div>
));
AbasTabs.displayName = "AbasTabs";

// ── Linha da tabela (MODIFICADA: nova coluna + botões) ──
const AluguelRow = memo(
  ({ aluguel, onEdit, onPagamento, onStatus, onComprovante, onHistorico }) => {
    const acoes = PROXIMAS_ACOES[aluguel.status] || [];

    return (
      <tr className={aluguel.status === "cancelado" ? styles.rowInativo : ""}>
        <td data-label="Nº">
          <code className={styles.numContrato}>
            #{String(aluguel.id).padStart(5, "0")}
          </code>
        </td>
        <td data-label="Locatário" className={styles.cellNome}>
          <div className={styles.locatarioInfo}>
            <span className={styles.locatarioNome}>
              {aluguel.locatario_nome || (
                <span className={styles.textMuted}>Não informado</span>
              )}
            </span>
            {aluguel.locatario_tel && (
              <span className={styles.locatarioTel}>
                {aluguel.locatario_tel}
              </span>
            )}
          </div>
        </td>
        <td data-label="Período">
          <div className={styles.periodoInfo}>
            <span>{fmtDate(aluguel.data_retirada)}</span>
            <span className={styles.periodoSeta}>→</span>
            <span>{fmtDate(aluguel.data_devolucao)}</span>
          </div>
        </td>

        {/* NOVA COLUNA: DATAS REAIS */}
        <td data-label="Datas Reais">
          {aluguel.data_retirada_real || aluguel.data_devolucao_real ? (
            <div className={styles.realDates}>
              {aluguel.data_retirada_real && (
                <span>Retirada: {fmtDateTime(aluguel.data_retirada_real)}</span>
              )}
              {aluguel.data_devolucao_real && (
                <span>
                  Devolvida: {fmtDateTime(aluguel.data_devolucao_real)}
                </span>
              )}
            </div>
          ) : (
            "—"
          )}
        </td>

        <td data-label="Itens">
          <div className={styles.itensResumo}>
            {(aluguel.itens || []).slice(0, 2).map((it) => (
              <span key={it.id} className={styles.itemTag}>
                {it.quantidade}× {it.produto_nome}
              </span>
            ))}
            {(aluguel.itens || []).length > 2 && (
              <span className={styles.itemTagMore}>
                +{aluguel.itens.length - 2}
              </span>
            )}
          </div>
        </td>
        <td data-label="Total">{fmtBRL(aluguel.valor_total)}</td>
        <td data-label="Pagamento">
          <SaldoBadge total={aluguel.valor_total} pago={aluguel.valor_pago} />
        </td>
        <td data-label="Status">
          <StatusBadge status={aluguel.status} />
        </td>
        <td data-label="Ações">
          <div className={styles.actions}>
            <button
              className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`}
              onClick={() => onEdit(aluguel)}
              aria-label="Editar"
              title="Editar"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Editar
            </button>

            {!["devolvido", "cancelado"].includes(aluguel.status) && (
              <button
                className={`${styles.btn} ${styles.btnSuccessGhost} ${styles.btnSm}`}
                onClick={() => onPagamento(aluguel)}
                aria-label="Registrar pagamento"
                title="Registrar pagamento"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                Pagamento
              </button>
            )}

            {/* NOVOS BOTÕES: COMPROVANTE E HISTÓRICO */}
            <button
              className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`}
              onClick={() => onComprovante(aluguel)}
              title="Comprovante de pagamento"
            >
              📄 Comprovante
            </button>
            <button
              className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`}
              onClick={() => onHistorico(aluguel)}
              title="Histórico de pagamentos"
            >
              📋 Histórico
            </button>

            {acoes.map((acao) => (
              <button
                key={acao.status}
                className={`${styles.btn} ${styles.btnSm} ${
                  acao.status === "cancelado"
                    ? styles.btnDangerGhost
                    : styles.btnPrimaryGhost
                }`}
                onClick={() => onStatus(aluguel, acao.status)}
                title={acao.label}
              >
                <span aria-hidden="true">{acao.icon}</span>
                {acao.label}
              </button>
            ))}
          </div>
        </td>
      </tr>
    );
  },
);
AluguelRow.displayName = "AluguelRow";

// ── Empty / Loading (colSpan ajustado para 9) ──
const EmptyState = memo(({ hasFilter }) => (
  <tr>
    <td colSpan="9">
      <div className={styles.emptyState}>
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <p>
          {hasFilter
            ? "Nenhum aluguel encontrado para esta busca."
            : "Nenhum aluguel cadastrado ainda."}
        </p>
        {hasFilter && <span>Tente outros termos ou limpe o filtro.</span>}
      </div>
    </td>
  </tr>
));
EmptyState.displayName = "EmptyState";

const LoadingState = memo(() => (
  <tr>
    <td colSpan="9">
      <div className={styles.loadingState}>
        <div
          className={styles.spinner}
          aria-label="Carregando..."
          role="status"
        />
        <span>Carregando...</span>
      </div>
    </td>
  </tr>
));
LoadingState.displayName = "LoadingState";

// ─── Modal principal (criar / editar aluguel) ─────────────────────────────────
// (mantido exatamente igual ao original)
const AluguelModal = memo(
  ({
    isOpen,
    form,
    onChange,
    onSubmit,
    onClose,
    alugaveisDisponiveis,
    totais,
    addItem,
    removeItem,
    updateItemQtd,
    addPagamento,
    removePagamento,
  }) => {
    const [searchProduto, setSearchProduto] = useState("");
    const [pgtoForm, setPgtoForm] = useState({
      forma: "Pix",
      valor: "",
      tipo: "sinal",
    });

    const isEditing = Boolean(form.id);

    const produtosFiltrados = alugaveisDisponiveis.filter((p) =>
      p.nome.toLowerCase().includes(searchProduto.toLowerCase()),
    );

    const handleField = useCallback(
      (field) => (e) => onChange({ ...form, [field]: e.target.value }),
      [form, onChange],
    );

    const handleAddPgto = () => {
      if (!pgtoForm.valor || Number(pgtoForm.valor) <= 0) return;
      addPagamento({ ...pgtoForm, valor: Number(pgtoForm.valor) });
      setPgtoForm({ forma: "Pix", valor: "", tipo: "sinal" });
    };

    if (!isOpen) return null;

    return (
      <div
        className={styles.modalOverlay}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-aluguel-title"
        onClick={(e) => e.target === e.currentTarget && onClose()}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
      >
        <div className={`${styles.modal} ${styles.modalLarge}`}>
          <div className={styles.modalHeader}>
            <h3 id="modal-aluguel-title" className={styles.modalTitle}>
              {isEditing ? "Editar Aluguel" : "Novo Aluguel"}
            </h3>
            <button
              className={styles.modalClose}
              onClick={onClose}
              type="button"
              aria-label="Fechar"
            >
              ×
            </button>
          </div>

          <form className={styles.modalBody} onSubmit={onSubmit} noValidate>
            <div className={styles.modalGrid}>
              {/* ── COLUNA ESQUERDA ── */}
              <div className={styles.modalCol}>
                {/* ── Dados do locatário ── */}
                <section className={styles.formSection}>
                  <h4 className={styles.formSectionTitle}>
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    Locatário
                    <span className={styles.formSectionHint}>
                      Campos opcionais
                    </span>
                  </h4>

                  <div className={styles.formGroup}>
                    <label htmlFor="loc-nome" className={styles.formLabel}>
                      Nome
                    </label>
                    <input
                      id="loc-nome"
                      className={styles.formInput}
                      placeholder="Nome completo"
                      value={form.locatario_nome}
                      onChange={handleField("locatario_nome")}
                      maxLength={200}
                    />
                  </div>

                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label htmlFor="loc-cpf" className={styles.formLabel}>
                        CPF
                      </label>
                      <input
                        id="loc-cpf"
                        className={styles.formInput}
                        placeholder="000.000.000-00"
                        value={form.locatario_cpf}
                        onChange={(e) =>
                          onChange({
                            ...form,
                            locatario_cpf: fmtCPF(e.target.value),
                          })
                        }
                        inputMode="numeric"
                        maxLength={14}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="loc-tel" className={styles.formLabel}>
                        Telefone
                      </label>
                      <input
                        id="loc-tel"
                        className={styles.formInput}
                        placeholder="(00) 00000-0000"
                        value={form.locatario_tel}
                        onChange={(e) =>
                          onChange({
                            ...form,
                            locatario_tel: fmtTel(e.target.value),
                          })
                        }
                        inputMode="numeric"
                        maxLength={15}
                      />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="loc-email" className={styles.formLabel}>
                      E-mail
                    </label>
                    <input
                      id="loc-email"
                      className={styles.formInput}
                      placeholder="email@exemplo.com"
                      type="email"
                      value={form.locatario_email}
                      onChange={handleField("locatario_email")}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="loc-end" className={styles.formLabel}>
                      Endereço
                    </label>
                    <input
                      id="loc-end"
                      className={styles.formInput}
                      placeholder="Rua, número, bairro, cidade"
                      value={form.locatario_end}
                      onChange={handleField("locatario_end")}
                    />
                  </div>
                </section>

                {/* ── Período ── */}
                <section className={styles.formSection}>
                  <h4 className={styles.formSectionTitle}>
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    Período
                    <span className={styles.formSectionRequired}>*</span>
                  </h4>

                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label
                        htmlFor="data-retirada"
                        className={styles.formLabel}
                      >
                        Retirada <span className={styles.required}>*</span>
                      </label>
                      <input
                        id="data-retirada"
                        className={styles.formInput}
                        type="datetime-local"
                        value={form.data_retirada}
                        onChange={handleField("data_retirada")}
                        required
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label
                        htmlFor="data-devolucao"
                        className={styles.formLabel}
                      >
                        Devolução <span className={styles.required}>*</span>
                      </label>
                      <input
                        id="data-devolucao"
                        className={styles.formInput}
                        type="datetime-local"
                        value={form.data_devolucao}
                        onChange={handleField("data_devolucao")}
                        required
                      />
                    </div>
                  </div>

                  {totais.dias > 0 && (
                    <p className={styles.diasInfo}>
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      {totais.dias} {totais.dias === 1 ? "dia" : "dias"} de
                      aluguel
                    </p>
                  )}
                </section>

                {/* ── Observações ── */}
                <section className={styles.formSection}>
                  <div className={styles.formGroup}>
                    <label htmlFor="obs" className={styles.formLabel}>
                      Observações
                    </label>
                    <textarea
                      id="obs"
                      className={`${styles.formInput} ${styles.formTextarea}`}
                      placeholder="Anotações adicionais sobre o aluguel..."
                      value={form.observacoes}
                      onChange={handleField("observacoes")}
                      rows={3}
                      maxLength={500}
                    />
                  </div>
                </section>
              </div>

              {/* ── COLUNA DIREITA ── */}
              <div className={styles.modalCol}>
                {/* ── Itens ── */}
                <section className={styles.formSection}>
                  <h4 className={styles.formSectionTitle}>
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <path d="M16 10a4 4 0 0 1-8 0" />
                    </svg>
                    Itens alugáveis
                    <span className={styles.formSectionRequired}>*</span>
                  </h4>

                  {/* Busca de produto */}
                  <div className={styles.searchBar}>
                    <input
                      className={styles.searchBarInput}
                      placeholder="Buscar item para adicionar..."
                      value={searchProduto}
                      onChange={(e) => setSearchProduto(e.target.value)}
                    />
                  </div>

                  {searchProduto && (
                    <div className={styles.produtoDropdown}>
                      {produtosFiltrados.length === 0 ? (
                        <div className={styles.produtoDropdownEmpty}>
                          Nenhum item encontrado
                        </div>
                      ) : (
                        produtosFiltrados.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            className={styles.produtoDropdownItem}
                            onClick={() => {
                              addItem(p);
                              setSearchProduto("");
                            }}
                          >
                            <span className={styles.produtoDropdownNome}>
                              {p.nome}
                            </span>
                            <span className={styles.produtoDropdownPreco}>
                              {fmtBRL(p.valor_diaria)}/dia
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}

                  {/* Lista de itens adicionados */}
                  {form.itens.length > 0 ? (
                    <div className={styles.itensList}>
                      {form.itens.map((it) => (
                        <div key={it.produto_id} className={styles.itemRow}>
                          <div className={styles.itemInfo}>
                            <span className={styles.itemNome}>
                              {it.produto_nome}
                            </span>
                            <span className={styles.itemPreco}>
                              {fmtBRL(it.valor_unitario)}/dia
                              {totais.dias > 1 && (
                                <>
                                  {" "}
                                  · {totais.dias}d ={" "}
                                  {fmtBRL(
                                    it.valor_unitario *
                                      it.quantidade *
                                      totais.dias,
                                  )}
                                </>
                              )}
                            </span>
                          </div>
                          <div className={styles.itemQtd}>
                            <button
                              type="button"
                              className={styles.qtdBtn}
                              onClick={() =>
                                updateItemQtd(it.produto_id, it.quantidade - 1)
                              }
                            >
                              −
                            </button>
                            <span className={styles.qtdValue}>
                              {it.quantidade}
                            </span>
                            <button
                              type="button"
                              className={styles.qtdBtn}
                              onClick={() =>
                                updateItemQtd(it.produto_id, it.quantidade + 1)
                              }
                            >
                              +
                            </button>
                          </div>
                          <button
                            type="button"
                            className={styles.removeBtn}
                            onClick={() => removeItem(it.produto_id)}
                            aria-label="Remover item"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.itensEmpty}>
                      <span>Busque e adicione itens acima</span>
                    </div>
                  )}
                </section>

                {/* ── Desconto ── */}
                <section className={styles.formSection}>
                  <div className={styles.formGroup}>
                    <label htmlFor="desconto" className={styles.formLabel}>
                      Desconto
                    </label>
                    <div className={styles.inputPrefix}>
                      <span className={styles.inputPrefixSymbol}>R$</span>
                      <input
                        id="desconto"
                        className={`${styles.formInput} ${styles.formInputPrefixed}`}
                        placeholder="0,00"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.desconto || ""}
                        onChange={handleField("desconto")}
                      />
                    </div>
                  </div>
                </section>

                {/* ── Pagamento inicial (só na criação) ── */}
                {!isEditing && (
                  <section className={styles.formSection}>
                    <h4 className={styles.formSectionTitle}>
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <rect x="1" y="4" width="22" height="16" rx="2" />
                        <line x1="1" y1="10" x2="23" y2="10" />
                      </svg>
                      Pagamento inicial
                      <span className={styles.formSectionHint}>
                        Opcional — pode pagar depois
                      </span>
                    </h4>

                    <div className={styles.pgtoRow}>
                      <select
                        className={styles.formInput}
                        value={pgtoForm.tipo}
                        onChange={(e) =>
                          setPgtoForm((p) => ({ ...p, tipo: e.target.value }))
                        }
                      >
                        {TIPOS_PGTO.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                      <select
                        className={styles.formInput}
                        value={pgtoForm.forma}
                        onChange={(e) =>
                          setPgtoForm((p) => ({ ...p, forma: e.target.value }))
                        }
                      >
                        {FORMAS.map((f) => (
                          <option key={f}>{f}</option>
                        ))}
                      </select>
                      <div className={styles.inputPrefix} style={{ flex: 1 }}>
                        <span className={styles.inputPrefixSymbol}>R$</span>
                        <input
                          className={`${styles.formInput} ${styles.formInputPrefixed}`}
                          placeholder="0,00"
                          type="number"
                          min="0"
                          step="0.01"
                          value={pgtoForm.valor}
                          onChange={(e) =>
                            setPgtoForm((p) => ({
                              ...p,
                              valor: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
                        onClick={handleAddPgto}
                      >
                        + Add
                      </button>
                    </div>

                    {form.pagamentos.length > 0 && (
                      <div className={styles.pgtosList}>
                        {form.pagamentos.map((p, idx) => (
                          <div key={idx} className={styles.pgtoItem}>
                            <span className={styles.pgtoTipo}>
                              {
                                TIPOS_PGTO.find((t) => t.value === p.tipo)
                                  ?.label
                              }
                            </span>
                            <span>{p.forma}</span>
                            <span className={styles.pgtoValor}>
                              {fmtBRL(p.valor)}
                            </span>
                            <button
                              type="button"
                              className={styles.removeBtn}
                              onClick={() => removePagamento(idx)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}

                {/* ── Resumo financeiro ── */}
                {form.itens.length > 0 && (
                  <div className={styles.resumoFinanceiro}>
                    <div className={styles.resumoRow}>
                      <span>Subtotal ({totais.dias}d)</span>
                      <span>{fmtBRL(totais.subtotal)}</span>
                    </div>
                    {Number(form.desconto) > 0 && (
                      <div
                        className={`${styles.resumoRow} ${styles.resumoDesconto}`}
                      >
                        <span>Desconto</span>
                        <span>− {fmtBRL(form.desconto)}</span>
                      </div>
                    )}
                    <div
                      className={`${styles.resumoRow} ${styles.resumoTotal}`}
                    >
                      <span>Total</span>
                      <span>{fmtBRL(totais.total)}</span>
                    </div>
                    {totais.pago > 0 && (
                      <>
                        <div
                          className={`${styles.resumoRow} ${styles.resumoPago}`}
                        >
                          <span>Pago agora</span>
                          <span>{fmtBRL(totais.pago)}</span>
                        </div>
                        <div
                          className={`${styles.resumoRow} ${styles.resumoSaldo}`}
                        >
                          <span>Saldo restante</span>
                          <span>{fmtBRL(totais.saldo)}</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnGhost}`}
                onClick={onClose}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={`${styles.btn} ${styles.btnPrimary}`}
              >
                {isEditing ? "Salvar alterações" : "Criar aluguel"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  },
);
AluguelModal.displayName = "AluguelModal";

// ─── Modal de pagamento adicional ─────────────────────────────────────────────
const PagamentoModal = memo(
  ({ isOpen, aluguel, form, onChange, onSubmit, onClose }) => {
    if (!isOpen || !aluguel) return null;

    const saldo = Number(aluguel.valor_total) - Number(aluguel.valor_pago);

    const handlePagarTotal = () => {
      onChange({ ...form, valor: saldo.toFixed(2) });
    };

    return (
      <div
        className={styles.modalOverlay}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-pgto-title"
        onClick={(e) => e.target === e.currentTarget && onClose()}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
      >
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <h3 id="modal-pgto-title" className={styles.modalTitle}>
              Registrar Pagamento
            </h3>
            <button
              className={styles.modalClose}
              onClick={onClose}
              type="button"
              aria-label="Fechar"
            >
              ×
            </button>
          </div>

          <form className={styles.modalBody} onSubmit={onSubmit} noValidate>
            <div className={styles.pgtoResumo}>
              <div className={styles.pgtoResumoItem}>
                <span>Contrato</span>
                <strong>#{String(aluguel.id).padStart(5, "0")}</strong>
              </div>
              <div className={styles.pgtoResumoItem}>
                <span>Total</span>
                <strong>{fmtBRL(aluguel.valor_total)}</strong>
              </div>
              <div className={styles.pgtoResumoItem}>
                <span>Já pago</span>
                <strong className={styles.textSuccess}>
                  {fmtBRL(aluguel.valor_pago)}
                </strong>
              </div>
              <div
                className={`${styles.pgtoResumoItem} ${styles.pgtoResumoDestaque}`}
              >
                <span>Saldo restante</span>
                <strong>{fmtBRL(saldo)}</strong>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Tipo do pagamento</label>
              <select
                className={styles.formInput}
                value={form.tipo}
                onChange={(e) => onChange({ ...form, tipo: e.target.value })}
              >
                {TIPOS_PGTO.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Forma de pagamento</label>
              <select
                className={styles.formInput}
                value={form.forma}
                onChange={(e) => onChange({ ...form, forma: e.target.value })}
              >
                {FORMAS.map((f) => (
                  <option key={f}>{f}</option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="pgto-valor" className={styles.formLabel}>
                Valor <span className={styles.required}>*</span>
              </label>
              <div className={styles.inputPrefix}>
                <span className={styles.inputPrefixSymbol}>R$</span>
                <input
                  id="pgto-valor"
                  className={`${styles.formInput} ${styles.formInputPrefixed}`}
                  placeholder="0,00"
                  type="number"
                  min="0.01"
                  max={saldo}
                  step="0.01"
                  value={form.valor}
                  onChange={(e) => onChange({ ...form, valor: e.target.value })}
                  required
                  autoFocus
                />
              </div>
              <span className={styles.formHint}>Máximo: {fmtBRL(saldo)}</span>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                onClick={handlePagarTotal}
              >
                Pagar total ({fmtBRL(saldo)})
              </button>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="pgto-obs" className={styles.formLabel}>
                Observação
              </label>
              <input
                id="pgto-obs"
                className={styles.formInput}
                placeholder="Opcional"
                value={form.observacao}
                onChange={(e) =>
                  onChange({ ...form, observacao: e.target.value })
                }
              />
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnGhost}`}
                onClick={onClose}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={`${styles.btn} ${styles.btnPrimary}`}
              >
                Confirmar pagamento
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  },
);
PagamentoModal.displayName = "PagamentoModal";

// ─── Componente principal ─────────────────────────────────────────────────────

const Aluguel = () => {
  const {
    alugueis,
    alugaveisDisponiveis,
    counts,
    loading,
    modalOpen,
    modalPgtoOpen,
    form,
    setForm,
    formPgto,
    setFormPgto,
    aluguelSelecionado,
    filtroStatus,
    setFiltroStatus,
    filtroTexto,
    setFiltroTexto,
    abaAtiva,
    setAbaAtiva,
    totaisForm,
    openModal,
    closeModal,
    handleSubmit,
    handleMudarStatus,
    openModalPgto,
    closeModalPgto,
    handleSubmitPgto,
    addItem,
    removeItem,
    updateItemQtd,
    addPagamentoForm,
    removePagamentoForm,
  } = useAluguel();

  const gerarComprovantePDF = (aluguel, dados) => {
    const win = window.open();
    win.document.write(`
    <html>
      <head>
        <title>Comprovante de Pagamento - Contrato #${String(aluguel.id).padStart(6, "0")}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 20px; }
          .header h1 { margin: 0; color: #2563eb; }
          .info { margin: 20px 0; }
          .info table { width: 100%; border-collapse: collapse; }
          .info td { padding: 6px; vertical-align: top; }
          .info td:first-child { font-weight: bold; width: 140px; }
          .pagamentos { margin-top: 20px; }
          .pagamentos table { width: 100%; border-collapse: collapse; }
          .pagamentos th, .pagamentos td { border: 1px solid #ccc; padding: 8px; text-align: left; }
          .pagamentos th { background: #f0f0f0; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Comprovante de Pagamento</h1>
          <p>Contrato #${String(aluguel.id).padStart(6, "0")}</p>
        </div>
        <div class="info">
          <table>
            <tr><td>Empresa:</td><td>${dados.comprovante.empresa.nome}</td></tr>
            <tr><td>Telefone:</td><td>${dados.comprovante.empresa.telefone || "—"}</td></tr>
            <tr><td>Locatário:</td><td>${dados.comprovante.locatario.nome}</td></tr>
            <tr><td>Telefone:</td><td>${dados.comprovante.locatario.telefone || "—"}</td></tr>
            <tr><td>Período:</td><td>${fmtDate(dados.comprovante.periodo.retirada)} → ${fmtDate(dados.comprovante.periodo.devolucao)}</td></tr>
            <tr><td>Valor total:</td><td>${fmtBRL(dados.comprovante.financeiro.valor_total)}</td></tr>
            <tr><td>Desconto:</td><td>${fmtBRL(dados.comprovante.financeiro.desconto)}</td></tr>
            <tr><td>Valor pago:</td><td>${fmtBRL(dados.comprovante.financeiro.valor_pago)}</td></tr>
            <tr><td>Saldo restante:</td><td>${fmtBRL(dados.comprovante.financeiro.saldo_restante)}</td></tr>
            <tr><td>Status:</td><td>${dados.comprovante.status}</td></tr>
          </table>
        </div>
        <div class="pagamentos">
          <h3>Pagamentos realizados</h3>
          <table>
            <thead><tr><th>Data</th><th>Forma</th><th>Valor</th><th>Tipo</th><th>Observação</th></tr></thead>
            <tbody>
              ${dados.comprovante.pagamentos
                .map(
                  (p) => `
                <tr>
                  <td>${fmtDateTime(p.created_at)}</td>
                  <td>${p.forma}</td>
                  <td>${fmtBRL(p.valor)}</td>
                  <td>${p.tipo}</td>
                  <td>${p.observacao || "—"}</td>
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
        </div>
        <div class="footer">
          <p>Comprovante gerado em ${new Date().toLocaleString("pt-BR")}</p>
        </div>
        <script>window.print();</script>
      </body>
    </html>
  `);
    win.document.close();
  };

  // NOVAS FUNÇÕES: comprovante e histórico
  const handleComprovante = async (aluguel) => {
    try {
      const data = await aluguelService.getComprovante(aluguel.id);
      gerarComprovantePDF(aluguel, data);
    } catch (err) {
      Swal.fire("Erro", "Não foi possível gerar o comprovante.", "error");
    }
  };

  const handleHistorico = async (aluguel) => {
    try {
      const pagamentos = await aluguelService.getPagamentos(aluguel.id);
      Swal.fire({
        title: `Histórico de Pagamentos - Contrato #${String(aluguel.id).padStart(6, "0")}`,
        html: `
          <div style="text-align: left;">
            ${
              pagamentos.length === 0
                ? "<p>Nenhum pagamento registrado.</p>"
                : `
              <table style="width:100%; border-collapse:collapse;">
                <thead><tr><th>Data</th><th>Forma</th><th>Valor</th><th>Tipo</th><th>Obs</th></tr></thead>
                <tbody>
                  ${pagamentos
                    .map(
                      (p) => `
                    <tr>
                      <td>${fmtDateTime(p.created_at)}</td>
                      <td>${p.forma}</td>
                      <td>${fmtBRL(p.valor)}</td>
                      <td>${p.tipo}</td>
                      <td>${p.observacao || "—"}</td>
                    </tr>
                  `,
                    )
                    .join("")}
                </tbody>
              </table>
            `
            }
          </div>
        `,
        width: 800,
      });
    } catch (err) {
      Swal.fire("Erro", "Não foi possível carregar o histórico.", "error");
    }
  };

  const STATUS_FILTROS = [
    { value: "todos", label: "Todos" },
    { value: "reservado", label: "Reservados" },
    { value: "confirmado", label: "Confirmados" },
    { value: "em_andamento", label: "Em andamento" },
    { value: "devolvido", label: "Devolvidos" },
    { value: "cancelado", label: "Cancelados" },
  ];

  return (
    <div className={styles.pageContainer}>
      {/* ── Header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderInfo}>
          <h1 className={styles.pageTitle}>Aluguéis</h1>
          <p className={styles.pageSubtitle}>
            {alugueis.length} {alugueis.length !== 1 ? "contratos" : "contrato"}{" "}
            encontrado{alugueis.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={() => openModal()}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            aria-hidden="true"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Novo Aluguel
        </button>
      </div>

      {/* ── Abas ── */}
      <AbasTabs value={abaAtiva} onChange={setAbaAtiva} counts={counts} />

      {/* ── Toolbar ── */}
      <div className={styles.pageToolbar}>
        <div className={styles.searchBar}>
          <span className={styles.searchBarIcon} aria-hidden="true">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </span>
          <input
            type="search"
            placeholder="Buscar por nome, telefone ou nº do contrato..."
            value={filtroTexto}
            onChange={(e) => setFiltroTexto(e.target.value)}
            className={styles.searchBarInput}
            aria-label="Buscar aluguéis"
          />
          {filtroTexto && (
            <button
              className={styles.searchBarClear}
              onClick={() => setFiltroTexto("")}
              type="button"
              aria-label="Limpar busca"
            >
              ×
            </button>
          )}
        </div>

        <div className={styles.statusFiltros}>
          {STATUS_FILTROS.map((f) => (
            <button
              key={f.value}
              type="button"
              className={`${styles.filtroBtn} ${filtroStatus === f.value ? styles.filtroBtnActive : ""}`}
              onClick={() => setFiltroStatus(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tabela (com nova coluna "Datas Reais") ── */}
      <div className={styles.tableWrapper}>
        <table
          className={styles.dataTable}
          role="grid"
          aria-label="Lista de aluguéis"
        >
          <thead>
            <tr>
              <th scope="col">Nº</th>
              <th scope="col">Locatário</th>
              <th scope="col">Período</th>
              <th scope="col">Datas Reais</th> {/* NOVA COLUNA */}
              <th scope="col">Itens</th>
              <th scope="col">Total</th>
              <th scope="col">Pagamento</th>
              <th scope="col">Status</th>
              <th scope="col">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <LoadingState />
            ) : alugueis.length === 0 ? (
              <EmptyState hasFilter={Boolean(filtroTexto)} />
            ) : (
              alugueis.map((a) => (
                <AluguelRow
                  key={a.id}
                  aluguel={a}
                  onEdit={openModal}
                  onPagamento={openModalPgto}
                  onStatus={handleMudarStatus}
                  onComprovante={handleComprovante}
                  onHistorico={handleHistorico}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Modais (mantidos originais) ── */}
      <AluguelModal
        isOpen={modalOpen}
        form={form}
        onChange={setForm}
        onSubmit={handleSubmit}
        onClose={closeModal}
        alugaveisDisponiveis={alugaveisDisponiveis}
        totais={totaisForm}
        addItem={addItem}
        removeItem={removeItem}
        updateItemQtd={updateItemQtd}
        addPagamento={addPagamentoForm}
        removePagamento={removePagamentoForm}
      />

      <PagamentoModal
        isOpen={modalPgtoOpen}
        aluguel={aluguelSelecionado}
        form={formPgto}
        onChange={setFormPgto}
        onSubmit={handleSubmitPgto}
        onClose={closeModalPgto}
      />
    </div>
  );
};

export default Aluguel;
